import { Component, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { environment } from '../../../environments/environment';
import * as firebaseui from 'firebaseui';
import { FirebaseApp } from '@angular/fire/app';
import { Auth } from '@angular/fire/auth';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('firebaseUIContainer', { static: true })
  firebaseUIContainer!: ElementRef;

  private ui?: firebaseui.auth.AuthUI;
  private authSubscription?: Subscription;
  private returnUrl: string = '/dashboard';
  private firebaseCompatInitialized = false;

  // Inject AngularFire services for Zone-wrapped operations
  private firebaseApp = inject(FirebaseApp);
  private auth = inject(Auth);

  constructor(
    public authService: AuthService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    // Initialize Firebase compat version using AngularFire app
    this.initializeFirebaseCompat();
  }

  private initializeFirebaseCompat(): void {
    if (!this.firebaseCompatInitialized) {
      try {
        // Check if Firebase compat has already been initialized
        if (!firebase.apps.length) {
          console.log('Initializing Firebase compat using AngularFire app...');
          // Use the same config as the AngularFire app to maintain consistency
          firebase.initializeApp(environment.firebase);
        }
        this.firebaseCompatInitialized = true;
        console.log('Firebase compat initialized successfully');
      } catch (error) {
        console.error('Error initializing Firebase compat:', error);
      }
    }
  }

  redirectToDashboard(): void {
    console.log('Redirecting to dashboard...');
    this.router.navigate(['/dashboard']);
  }

  ngOnInit(): void {
    // Get return URL from query params or default to dashboard
    this.returnUrl =
      this.route.snapshot.queryParams['returnUrl'] || '/dashboard';
    console.log('Login component initialized with return URL:', this.returnUrl);

    // Check if user is already authenticated
    if (this.authService.isAuthenticated) {
      console.log(
        'User already authenticated, redirecting to:',
        this.returnUrl
      );
      this.authService.handlePostLoginRedirect(this.returnUrl);
      return;
    }

    // Wait for auth initialization before proceeding
    this.authService.waitForAuthInitialization().then((user) => {
      if (user) {
        console.log(
          'User authenticated after initialization, redirecting to:',
          this.returnUrl
        );
        this.authService.handlePostLoginRedirect(this.returnUrl);
      }
    });
  }

  ngAfterViewInit(): void {
    // Only initialize FirebaseUI if user is not already authenticated
    if (!this.authService.currentUser) {
      console.log('Initializing FirebaseUI for unauthenticated user');
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        this.initializeFirebaseUI();
      }, 200);
    } else {
      console.log(
        'User already authenticated, skipping FirebaseUI initialization'
      );
    }
  }

  ngOnDestroy(): void {
    // Clean up FirebaseUI instance
    if (this.ui) {
      this.ui.delete();
    }

    // Clean up subscription
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
  }

  private initializeFirebaseUI(): void {
    // Check if the container element exists
    if (!this.firebaseUIContainer || !this.firebaseUIContainer.nativeElement) {
      console.log(
        'FirebaseUI container not available, skipping initialization'
      );
      return;
    }

    console.log('Starting FirebaseUI initialization...');

    // Ensure Firebase compat is initialized
    this.initializeFirebaseCompat();

    // Get or create AuthUI instance using compat auth
    // Note: FirebaseUI requires the compat Auth instance, but we ensure it uses
    // the same configuration as AngularFire for Zone wrapping consistency
    try {
      const compatAuth = firebase.auth();
      this.ui =
        firebaseui.auth.AuthUI.getInstance() ||
        new firebaseui.auth.AuthUI(compatAuth);

      // Start the UI with return URL
      this.ui.start(
        this.firebaseUIContainer.nativeElement,
        this.getFirebaseUIConfig(this.returnUrl)
      );
    } catch (error) {
      console.error('FirebaseUI initialization error:', error);
    }
  }
  // FirebaseUI configuration
  getFirebaseUIConfig(
    returnUrl: string = '/dashboard'
  ): firebaseui.auth.Config {
    return {
      signInOptions: [
        firebase.auth.GoogleAuthProvider.PROVIDER_ID,
      ],
      signInSuccessUrl: returnUrl,
      signInFlow: 'popup',
      credentialHelper: firebaseui.auth.CredentialHelper.GOOGLE_YOLO,
      callbacks: {
        signInSuccessWithAuthResult: (authResult, redirectUrl) => {
          // Handle successful sign in
          console.log('User signed in successfully:', authResult.user);
          console.log('Redirecting to:', returnUrl);

          // Validate and sanitize the return URL
          const safeReturnUrl = this.getSafeReturnUrl(returnUrl);

          // Navigate to the return URL after successful sign in
          this.router.navigate([safeReturnUrl]);
          return false; // Don't redirect automatically, we handle it manually
        },
        signInFailure: (error) => {
          console.error('Sign in failed:', error);
          return Promise.resolve();
        },
      },
      tosUrl: '/terms-of-service',
      privacyPolicyUrl: '/privacy-policy',
    };
  }

  // Helper method to validate and sanitize return URLs
  private getSafeReturnUrl(returnUrl: string): string {
    // List of allowed return URLs for security
    const allowedUrls = [
      '/dashboard',
      '/profile',
      '/settings',
      '/admin',
      '/users',
      '/cards',
    ];

    // If returnUrl is empty or invalid, default to dashboard
    if (!returnUrl || returnUrl === '/login' || returnUrl === '/') {
      return '/dashboard';
    }

    // Check if the return URL is in our allowed list or starts with allowed paths
    const isAllowed = allowedUrls.some(
      (url) => returnUrl === url || returnUrl.startsWith(url + '/')
    );

    return isAllowed ? returnUrl : '/dashboard';
  }
}
